<template>
  <v-card class="rounded-lg fill-height">
    <v-card-title class="pa-4">
      <v-row
        align="center"
        no-gutters
      >
        <v-col>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('coach_insights.table.title') }}
          </div>
        </v-col>
        <v-col
          cols="auto"
          class="pl-2"
        >
          <variable-selector
            v-if="expandedVariables.length > 0"
            :variables="expandedVariables"
            :layout-variables.sync="selectedVariablesList"
            :excluded-variables="[]"
            :use-long-text="true"
            @update="$emit('variables-updated')"
          />
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-data-table
        :headers="tableHeaders"
        :items="coachStats"
        :loading="loading"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        sort-by="coach.last_name"
        dense
      >
        <template v-slot:item="{ item }">
          <tr
            style="cursor: pointer;"
            @click="$emit('coach-click', item)"
          >
            <td
              style="width: 0px"
              class="pr-0"
            >
              <v-avatar
                size="32px"
                class="mr-2"
                color="grey lighten-2"
              >
                <v-img :src="item.coach.photo_url">
                  <template v-slot:placeholder>
                    <v-icon x-small>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </td>
            <td>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  {{ item.coach.last_name }} {{ item.coach.first_name }}
                </div>
              </div>
            </td>
            <td>
              <v-tooltip
                top
                :disabled="(item.coach.teams || []).length <= 2"
              >
                <template v-slot:activator="{ on }">
                  <div
                    class="d-flex flex-wrap"
                    v-on="on"
                  >
                    <v-chip
                      v-for="team in (item.coach.teams || []).slice(0, 2)"
                      :key="team.id"
                      x-small
                      outlined
                      class="mr-1 my-1"
                    >
                      {{ team.short_name || team.name }}
                    </v-chip>
                    <span
                      v-if="(item.coach.teams || []).length > 2"
                      class="grey--text text-caption align-self-center"
                    >
                      +{{ item.coach.teams.length - 2 }}
                    </span>
                  </div>
                </template>
                <span>{{ (item.coach.teams || []).map(t => t.short_name || t.name).join(', ') }}</span>
              </v-tooltip>
            </td>
            <td
              v-for="variable in selectedVariables"
              :key="variableStatsKey(variable)"
              :style="isTagBreakdownVar(variable) ? 'min-width: 180px' : 'min-width: 100px'"
            >
              <template v-if="isTagBreakdownVar(variable)">
                <tag-breakdown-cell
                  :stats="item.stats"
                  :tag="getTagForVariable(variable)"
                  :stat-prefix="variableStatsPrefix(variable)"
                  :suffix="variable.suffix"
                />
              </template>
              <template v-else>
                <span
                  class="d-block"
                  :class="{ 'grey--text text--lighten-1': item.stats[variableStatsKey(variable)] == null }"
                >
                  {{ formatStatValue(item.stats[variableStatsKey(variable)], variable) }}
                </span>
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import TagBreakdownCell from './TagBreakdownCell.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'

export default {
  name: 'CoachInsightsTable',
  components: { TagBreakdownCell, VariableSelector },
  props: {
    coachStats: {
      type: Array,
      required: true
    },
    selectedVariables: {
      type: Array,
      default: () => []
    },
    variables: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    expandedVariables() {
      const result = []
      this.variables.forEach(v => {
        result.push(v)
        if (v.suffix_agg && typeof v.suffix_agg === 'object') {
          Object.keys(v.suffix_agg).forEach(aggKey => {
            result.push({ ...v, aggregation: aggKey })
          })
        }
      })
      return result
    },
    selectedVariablesList: {
      get() {
        return this.selectedVariables
      },
      set(value) {
        this.$emit('update:selectedVariables', value)
      }
    },
    tableHeaders() {
      const headers = [
        { text: '', value: 'avatar', sortable: false, width: '0px' },
        { text: this.$t('coach_insights.table.coach'), value: 'coach.last_name', sortable: true, width: '250px' },
        { text: this.$t('coach_insights.table.assigned_to'), value: 'teams', sortable: false, width: '150px' }
      ]

      this.selectedVariables.forEach(variable => {
        const categoryKey = variable.category.toLowerCase()
        let text = this.$t(`${categoryKey}.insight.${variable.value}`) || variable.text || variable.value
        if (variable.aggregation) {
          const aggLabel = this.getAggregationLabel(variable.aggregation)
          text += ` (${aggLabel})`
        }
        headers.push({
          text,
          value: this.variableStatsKey(variable),
          sortable: false,
          width: this.isTagBreakdownVar(variable) ? '180px' : '120px'
        })
      })

      return headers
    }
  },
  methods: {
    variableStatsKey(variable) {
      const varKey = variable.aggregation ? `${variable.value}_${variable.aggregation}` : variable.value
      return `${variable.category.toLowerCase()}.${varKey}`
    },
    variableStatsPrefix(variable) {
      return `${variable.category.toLowerCase()}.${variable.value}`
    },
    isTagBreakdownVar(variable) {
      return !!variable.aggregation
    },
    getTagForVariable(variable) {
      return {
        id: variable.aggregation,
        label: this.getAggregationLabel(variable.aggregation)
      }
    },
    getAggregationLabel(aggregation) {
      if (aggregation === 'group_id') return this.$t('insights.by_group')
      return aggregation.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    formatStatValue(value, variable) {
      if (value == null) return 'n/a'

      if (typeof value === 'object') {
        if (typeof value.total === 'number') {
          const formatted = Number.isInteger(value.total) ? value.total : Math.round(value.total * 100) / 100
          return variable.suffix ? `${formatted} ${variable.suffix}` : formatted
        }
        return 'n/a'
      }

      if (typeof value === 'number') {
        const formatted = Number.isInteger(value) ? value : Math.round(value * 100) / 100
        return variable.suffix ? `${formatted} ${variable.suffix}` : formatted
      }

      return value
    }
  }
}
</script>
